import * as THREE from "three";

import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader, KTX2Loader, OrbitControls } from "three/addons";

let canvas,
  camera,
  scene,
  gltf,
  renderer,
  controls,
  mixer,
  hasClickedOnce,
  fitDistance,
  autoRotateTimeout;
const clock = new THREE.Clock();

init().catch(function (err) {
  console.error(err);
});

function setupIntersectionObserver() {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          renderer.setAnimationLoop(render);
        } else {
          renderer.setAnimationLoop(null);
        }
      });
    },
    {
      threshold: 0.5,
    },
  );

  observer.observe(canvas);
}

function setMessages() {
  const threeDInfo = document.getElementById("js-three-d-info");

  if (threeDInfo) {
    threeDInfo.classList.add("is-active");
  }
}

/**
 * Init function.
 */
async function init() {
  canvas = document.querySelector("canvas#product-3d-model");
  const model = canvas.getAttribute("data-model");
  const minPolarAngle = canvas.getAttribute("data-min-polar-angle");
  const maxPolarAngle = canvas.getAttribute("data-max-polar-angle");

  /**
   * Renderer
   */
  renderer = new THREE.WebGLRenderer({
    antialias: true,
    canvas: canvas,
  });
  renderer.setPixelRatio(window.devicePixelRatio);
  const minSize = Math.min(
    canvas.parentElement.offsetWidth,
    canvas.parentElement.offsetHeight,
  );
  renderer.setSize(minSize, minSize);
  // renderer.toneMapping = THREE.ACESFilmicToneMapping;
  // renderer.toneMappingExposure = 1;
  const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

  camera = new THREE.PerspectiveCamera(
    35,
    canvas.parentElement.offsetWidth / canvas.parentElement.offsetHeight,
    0.25,
    20,
  );
  camera.aspect = 1;
  camera.position.set(0, 0, 4);

  camera.updateProjectionMatrix();

  scene = new THREE.Scene();

  scene.background = new THREE.Color(0xfee2bc);

  const gltfLoader = new GLTFLoader();

  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath("https://www.gstatic.com/draco/v1/decoders/"); // Google-hosted Draco decoder
  gltfLoader.setDRACOLoader(dracoLoader);

  const ktx2Loader = new KTX2Loader()
    .setTranscoderPath(new URL("../libs/basis/", import.meta.url).href)
    .detectSupport(renderer);
  gltfLoader.setKTX2Loader(ktx2Loader);

  gltf = await gltfLoader.loadAsync(model);

  gltf.scene.traverse(function (node) {
    if (node.isMesh && node.material.map) {
      node.material.map.anisotropy = maxAnisotropy;
    }
  });
  scene.add(gltf.scene);
  fitCameraToObject(gltf.scene);

  mixer = new THREE.AnimationMixer(gltf.scene);
  gltf.animations.forEach((clip) => {
    mixer.clipAction(clip).play();
  });

  addControls(minPolarAngle, maxPolarAngle);
  controls.enabled = false;
  canvas.style.touchAction = "auto";

  canvas.parentElement.addEventListener(
    "click",
    () => {
      if (hasClickedOnce) {
        return;
      }
      controls.enabled = true;
      canvas.style.touchAction = "none";
      canvas.classList.remove("cursor-pointer");
      canvas.classList.add("cursor-grab");
      setMessages();
      hasClickedOnce = true;
    },
    { once: true },
  );

  setupIntersectionObserver();
  render();

  window.addEventListener("resize", onWindowResize);
}

function fitCameraToObject(object, padding = 1) {
  const box = new THREE.Box3().setFromObject(object);
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const center = sphere.center;

  // Center model at origin so rotation pivots around its centroid.
  object.position.sub(center);

  const fov = camera.fov * (Math.PI / 180);
  const aspect = camera.aspect || 1;
  const halfFovY = fov / 2;
  const halfFovX = Math.atan(Math.tan(halfFovY) * aspect);
  const halfFov = Math.min(halfFovY, halfFovX);
  fitDistance = (sphere.radius / Math.sin(halfFov)) * padding;

  camera.position.set(0, 0, fitDistance);
  camera.near = Math.max(fitDistance / 100, 0.01);
  camera.far = fitDistance * 100;
  camera.updateProjectionMatrix();
}

/**
 * Controls
 */
function addControls(minPolarAngle, maxPolarAngle) {
  controls = new OrbitControls(camera, renderer.domElement);
  controls.minPolarAngle = Math.PI / 2;
  if (minPolarAngle) {
    controls.minPolarAngle -= minPolarAngle;
  }
  controls.maxPolarAngle = 1.5;
  if (maxPolarAngle) {
    controls.maxPolarAngle = Math.PI / 2 + Number(maxPolarAngle);
  }
  controls.minDistance = fitDistance * 0.5;
  controls.maxDistance = fitDistance * 2;
  controls.target.set(0, 0, 0);
  controls.enablePan = false;
  controls.enableRotate = true;
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.zoomSpeed = 2;
  controls.rotateSpeed = 0.6;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 2.75;

  controls.addEventListener("start", () => {
    controls.autoRotate = false;
    if (autoRotateTimeout) {
      clearTimeout(autoRotateTimeout);
    }
  });
  controls.addEventListener("end", () => {
    autoRotateTimeout = setTimeout(() => {
      controls.autoRotate = true;
    }, 3000);
  });

  controls.update();
}

/**
 * Respond to window resize event.
 */
function onWindowResize() {
  camera.updateProjectionMatrix();

  const minSize = Math.min(
    canvas.parentElement.offsetWidth,
    canvas.parentElement.offsetHeight,
  );
  renderer.setSize(minSize, minSize);

  render();
}

/**
 * Render function.
 */
function render() {
  const delta = clock.getDelta();
  if (mixer) {
    mixer.update(delta);
  }

  if (controls) {
    controls.update();
  }

  renderer.render(scene, camera);
}
